import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { IMG } from "@/data";
import { speakBlob, stopSpeaking, onState, onAmp } from "@/lib/voice";
import { requestTalk, pollTalk } from "@/lib/talk";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Host({ message, navTo, scene }) {
  const [open, setOpen] = useState(false);
  const [arrived, setArrived] = useState(false);
  const [thread, setThread] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [micErr, setMicErr] = useState(false);
  const [typingOpen, setTypingOpen] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("rom_voice") !== "0");
  const [lipVideo, setLipVideo] = useState(null);
  const suppressAuto = useRef(0);
  const bodyRef = useRef(null);
  const sessionRef = useRef(null);
  const avatarRef = useRef(null);
  const recRef = useRef(null);
  const voiceOnRef = useRef(voiceOn);

  useEffect(() => {
    voiceOnRef.current = voiceOn;
  }, [voiceOn]);

  useEffect(() => {
    if (!sessionRef.current) {
      let sid = localStorage.getItem("rom_session");
      if (!sid) {
        sid = "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem("rom_session", sid);
      }
      sessionRef.current = sid;
    }
  }, []);

  // she walks in when the welcome begins, and stays with you for the whole visit
  useEffect(() => {
    const onWelcome = () => setArrived(true);
    window.addEventListener("rom-welcome", onWelcome);
    // গাইডেড-প্রবাহের খবর — মঞ্চের বুকিং প্রবাহ বলে যায়
    const onSay = (e) => {
      const text = e.detail?.text;
      if (!text) return;
      setOpen(true);
      setThread((t) => (t.some((m) => m.text === text && m.role === "host") ? t : [...t, { id: "say" + Date.now(), role: "host", text }]));
    };
    window.addEventListener("rom-host-say", onSay);
    return () => {
      window.removeEventListener("rom-welcome", onWelcome);
      window.removeEventListener("rom-host-say", onSay);
    };
  }, []);

  useEffect(() => {
    if (scene !== "gate") setArrived(true);
  }, [scene]);

  useEffect(() => {
    if (!message) return;
    if (Date.now() - suppressAuto.current < 3000) return;
    setOpen(true);
    setThread((t) =>
      t.some((m) => m.autoKey === message.autoKey)
        ? t
        : [...t, { id: "ctx" + message.autoKey, role: "host", text: message.text }],
    );
  }, [message?.autoKey]);

  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread, busy, open]);

  useEffect(() => onState((s) => setSpeaking(s === "playing")), []);

  useEffect(
    () =>
      onAmp((a) => {
        if (avatarRef.current && !lipVideo)
          avatarRef.current.style.transform = `scale(${1 + a * 0.09})`;
      }),
    [lipVideo],
  );

  useEffect(
    () => () => {
      try {
        recRef.current?.stop();
      } catch {}
    },
    [],
  );

  const speakWithLips = async (text, hostId) => {
    const talk = await requestTalk(text);
    if (!talk) return false;
    if (talk.video_url) {
      setLipVideo(talk.video_url);
      return true;
    }
    if (talk.talk_id) {
      pollTalk(talk.talk_id).then((url) => {
        if (url)
          setThread((ts) => ts.map((m) => (m.id === hostId ? { ...m, video: url } : m)));
      });
    }
    return false;
  };

  const speakReply = async (text, hostId) => {
    if (!voiceOnRef.current || !text) return;
    const withLips = await speakWithLips(text, hostId);
    if (withLips) return;
    try {
      const res = await fetch(`${API}/host/voice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) return;
      await speakBlob(await res.blob());
    } catch {}
  };

  const ask = async (text, sceneOverride) => {
    const msg = text.trim();
    if (!msg || busy) return;
    const hostId = "h" + Date.now();
    setThread((t) => [
      ...t,
      { id: "u" + Date.now(), role: "user", text: msg },
      { id: hostId, role: "host", text: "", pending: true },
    ]);
    setInput("");
    setBusy(true);
    let full = "";
    try {
      const res = await fetch(`${API}/host/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: msg,
          scene: sceneOverride || scene || "gate",
          session_id: sessionRef.current,
        }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const line = buf.slice(0, idx).trim();
          buf = buf.slice(idx + 2);
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) {
              full += delta;
              setThread((t) =>
                t.map((m) => (m.id === hostId ? { ...m, text: m.text + delta, pending: false } : m)),
              );
            }
          } catch {}
        }
      }
      setThread((t) => t.map((m) => (m.id === hostId ? { ...m, pending: false } : m)));
    } catch {
      setThread((t) =>
        t.map((m) =>
          m.id === hostId
            ? { ...m, pending: false, text: m.text || "এই মুহূর্তে কথা বলতে একটু কষ্ট হচ্ছে — একটু পরে আবার বলবেন?" }
            : m,
        ),
      );
    }
    setBusy(false);
    speakReply(full.replace(/\*+/g, ""), hostId);
  };

  const onChip = (chip) => {
    suppressAuto.current = Date.now();
    setOpen(true);
    // দৃশ্য-অ্যাকশন চিপ — নেভিগেশন নয়, পাতার ভেতরে অনুভূতি খোলে
    if (chip.action) {
      window.dispatchEvent(new CustomEvent("rom-host-action", { detail: { action: chip.action } }));
      if (chip.send) ask(chip.send, chip.view || scene);
      return;
    }
    navTo(chip.view, chip.label, chip.shop);
    ask(chip.label, chip.view);
  };

  const toggleMic = () => {
    if (listening) {
      try {
        recRef.current?.stop();
      } catch {}
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setMicErr(true);
      return;
    }
    setMicErr(false);
    stopSpeaking();
    setLipVideo(null);
    const rec = new SR();
    rec.lang = "bn-BD";
    rec.interimResults = true;
    rec.onresult = (e) => {
      let final = "";
      let interim = "";
      for (let i = 0; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      setInput(final || interim);
      if (final) {
        try {
          rec.stop();
        } catch {}
      }
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  const chips = message?.chips || [];
  const visibleThread = thread.slice(-4);

  return (
    <div className="fixed bottom-5 left-4 z-[70] flex flex-col items-start gap-3 sm:left-6">
      <AnimatePresence>
        {lipVideo && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14, scale: 0.96 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-[250px] overflow-hidden rounded-2xl border border-gold/40 shadow-[0_24px_60px_rgba(0,0,0,0.7)]"
            data-testid="host-lipvideo"
          >
            <video
              src={lipVideo}
              autoPlay
              playsInline
              onEnded={() => setLipVideo(null)}
              onError={() => setLipVideo(null)}
              className="w-full"
              onClick={() => setLipVideo(null)}
            />
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-dusk/90 to-transparent px-3 pb-2 pt-6">
              <p className="text-[11px] text-sand">সুচরিতা • ঠোঁটের সঙ্গে বলছেন</p>
              <button
                onClick={() => setLipVideo(null)}
                aria-label="বন্ধ করুন"
                className="rounded-full bg-dusk/60 p-1 text-sand/80"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {arrived && open && (
          <motion.div
            key="bubbles"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex w-[330px] max-w-[calc(100vw-32px)] flex-col gap-2"
            data-testid="host-speech-card"
          >
            <button
              data-testid="host-card-close"
              onClick={() => setOpen(false)}
              className="absolute -right-1.5 -top-2 z-10 rounded-full border border-gold/30 bg-plaster p-1 text-sand/60 shadow-md transition-colors hover:text-sand"
              aria-label="বন্ধ করুন"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>

            {visibleThread.length === 0 && (
              <div className="self-start rounded-2xl rounded-bl-sm border border-gold/25 bg-plaster/95 px-4 py-3 text-sm leading-relaxed text-sand/95 shadow-xl backdrop-blur-md">
                নমস্কার — বলুন তো, কেমন লাগছে আমাদের গ্রাম? মুখে বলতে চাইলে মাইক-চিহ্নে চাপুন।
              </div>
            )}
            {visibleThread.map((m) => (
              <div key={m.id} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className="max-w-[92%]">
                  <div
                    data-testid={`host-message-${m.role}`}
                    className={`rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-lg backdrop-blur-md ${
                      m.role === "user"
                        ? "rounded-br-sm border border-gold/30 bg-gold/15 text-sand"
                        : "rounded-bl-sm border border-gold/25 bg-plaster/95 text-sand/95"
                    }`}
                  >
                    {m.pending && !m.text ? (
                      <span className="flex gap-1 py-1" data-testid="host-typing-indicator">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gold" />
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gold [animation-delay:0.2s]" />
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gold [animation-delay:0.4s]" />
                      </span>
                    ) : (
                      m.text.replace(/\*+/g, "")
                    )}
                  </div>
                  {m.role === "host" && m.video && !m.pending && (
                    <button
                      data-testid={`host-msg-video-${m.id}`}
                      onClick={() => setLipVideo(m.video)}
                      className="mt-1.5 flex items-center gap-1.5 rounded-full border border-gold/35 bg-gold/10 px-3 py-1 text-[10px] text-gold transition-all duration-300 hover:bg-gold hover:text-dusk"
                    >
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5.5v13l11-6.5-11-6.5Z" />
                      </svg>
                      ঠোঁটের সঙ্গে দেখুন
                    </button>
                  )}
                </div>
              </div>
            ))}

            {chips.length > 0 && (
              <div className="flex flex-wrap gap-2 pl-1">
                {chips.map((chip) => (
                  <button
                    key={chip.label}
                    data-testid={`host-chip-${chip.shop || chip.view || chip.action || "send"}`}
                    onClick={() => onChip(chip)}
                    className="rounded-full border border-gold/35 bg-gold/10 px-3.5 py-1.5 text-xs text-gold backdrop-blur-md transition-all duration-300 hover:bg-gold hover:text-dusk"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {arrived && (typingOpen || listening) && (
          <motion.div
            key="typeline"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25 }}
            className="flex w-[330px] max-w-[calc(100vw-32px)] items-center gap-2 rounded-full border border-gold/25 bg-plaster/95 px-3 py-2 shadow-xl backdrop-blur-md"
          >
            <input
              data-testid="host-chat-input"
              autoFocus
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={listening ? "শুনছি — বলুন…" : "সুচরিতাকে কিছু বলুন…"}
              className="min-w-0 flex-1 bg-transparent px-2 text-sm text-sand placeholder:text-sand/40 focus:outline-none"
            />
            <button
              data-testid="host-chat-send"
              onClick={() => ask(input)}
              disabled={busy}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-vermilion text-alpana transition-transform duration-300 hover:scale-105 disabled:opacity-50"
              aria-label="পাঠান"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z" />
              </svg>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {arrived && (
          <motion.div
            key="presence"
            initial={{ x: -90, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -90, opacity: 0 }}
            transition={{ type: "spring", stiffness: 70, damping: 12 }}
            className="flex items-center gap-2.5"
            data-testid="host-presence"
          >
            <button
              data-testid="host-avatar-button"
              ref={avatarRef}
              onClick={() => setOpen((o) => !o)}
              className={`relative block h-14 w-14 shrink-0 overflow-hidden rounded-full ring-2 shadow-[0_10px_30px_rgba(0,0,0,0.6)] transition-colors duration-300 will-change-transform ${speaking || lipVideo ? "ring-vermilion" : "ring-gold/60"}`}
              aria-label="সুচরিতাকে কিছু জিজ্ঞেস করুন"
            >
              <img src={IMG.host} alt="সুচরিতা" className="h-full w-full object-cover object-top" />
              {!speaking && !lipVideo && (
                <span className="absolute right-0 top-0 h-3 w-3 animate-pulse rounded-full bg-gold ring-2 ring-dusk" />
              )}
              {(speaking || lipVideo) && (
                <span data-testid="host-speaking-badge" className="absolute bottom-0.5 right-0.5 h-3 w-3 animate-flicker rounded-full bg-vermilion ring-2 ring-dusk" />
              )}
            </button>

            <div className="flex items-center gap-2 rounded-full border border-gold/25 bg-plaster/90 px-3.5 py-2 shadow-lg backdrop-blur-md">
              <span className="text-xs text-sand">
                সুচরিতা <span className="text-gold/60">{speaking || lipVideo ? "• বলছেন…" : listening ? "• শুনছি…" : ""}</span>
              </span>
              <span className="h-3 w-px bg-sand/20" />
              <button
                data-testid="host-mic-button"
                onClick={toggleMic}
                aria-label={listening ? "শোনা বন্ধ করুন" : "বাংলায় মুখে বলুন"}
                className={`flex h-7 w-7 items-center justify-center rounded-full border transition-all duration-300 ${
                  listening
                    ? "animate-pulse border-vermilion bg-vermilion/20 text-vermilion"
                    : "border-sand/25 text-sand/70 hover:border-gold hover:text-gold"
                }`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <rect x="9" y="3" width="6" height="11" rx="3" />
                  <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
                </svg>
              </button>
              <button
                data-testid="host-typing-toggle"
                onClick={() => setTypingOpen((t) => !t)}
                aria-label="লিখে বলুন"
                className={`flex h-7 w-7 items-center justify-center rounded-full border transition-all duration-300 ${
                  typingOpen
                    ? "border-gold bg-gold/15 text-gold"
                    : "border-sand/25 text-sand/70 hover:border-gold hover:text-gold"
                }`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="6" width="18" height="12" rx="2" />
                  <path d="M7 10h.01M11 10h.01M15 10h.01M7 14h10" />
                </svg>
              </button>
              <button
                data-testid="host-voice-mute"
                onClick={() => {
                  const nv = !voiceOn;
                  setVoiceOn(nv);
                  localStorage.setItem("rom_voice", nv ? "1" : "0");
                  if (!nv) stopSpeaking();
                }}
                aria-label={voiceOn ? "গলা বন্ধ করুন" : "গলা চালু করুন"}
                className={`flex h-7 w-7 items-center justify-center rounded-full border transition-all duration-300 ${
                  voiceOn ? "border-sand/25 text-gold/80 hover:border-gold" : "border-sand/15 text-sand/35 hover:border-gold"
                }`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 5 6 9H3v6h3l5 4V5Z" />
                  {voiceOn ? (
                    <path d="M15.5 8.5a5 5 0 0 1 0 7M18 6a8.5 8.5 0 0 1 0 12" />
                  ) : (
                    <path d="m16 9 6 6M22 9l-6 6" />
                  )}
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {micErr && (
        <p data-testid="mic-unsupported" className="text-[10px] text-vermilion">
          এই ব্রাউজারে কণ্ঠ-ইনপুট নেই — কিবোর্ড-চিহ্নে চেপে লিখে বলুন।
        </p>
      )}
    </div>
  );
}
